import React, { useEffect, useRef, useState } from 'react';
import GraficoDoLogo from './GraficoDoLogo';
import { SERVICOS, TEMPO_DE_CADA_MS, servicoDaVez } from '../utils/servicos';

/**
 * A tela de carregamento — uma só, para os dois momentos.
 *
 * ── Por que ela existe ────────────────────────────────────────────────────
 *
 * Num refresh estando logada, a pessoa via DUAS telas diferentes em sequência:
 * primeiro um anel cinza com "Carregando..." enquanto a sessão resolvia,
 * depois um overlay roxo com o logo enquanto os dados vinham. Duas esperas
 * seguidas, com desenhos diferentes, parecem duas falhas.
 *
 * ── O anel, e não uma barra ──────────────────────────────────────────────
 *
 * Cheguei a trocar por uma barra de progresso, com o argumento de que anel
 * não informa quanto falta. Ela preferiu o anel, e o argumento perde força
 * quando se olha o conjunto: o progresso REAL continua na mensagem, que é a
 * primeira busca ainda pendente. A barra dizia a mesma coisa duas vezes.
 *
 * E o anel serve aos dois momentos sem exceção. A barra precisava de um modo
 * indeterminado para a espera da sessão, onde não há etapas para contar —
 * uma peça a mais para manter, que existia só para cobrir um caso.
 *
 * ── A tela é CONSTANTE; só o texto troca ─────────────────────────────────
 *
 * Havia um `key={mensagem}` neste parágrafo. Chave nova faz o React desmontar
 * e remontar o elemento, então a animação de entrada rodava a cada frase — e
 * o relato foi exato: "a cada mudança de frase a tela pisca como se tivesse
 * um loading pra cada frase".
 *
 * Sem chave, o React só troca o texto do nó que já existe. Nada remonta, nada
 * reanima, e o logo e o anel nem sabem que a frase mudou.
 *
 * ── O desfile de serviços tem chave, e não contradiz o de cima ───────────
 *
 * A linha dos serviços PRECISA reanimar a cada troca — é isso que faz uma
 * sumir e a outra aparecer. O que causava a piscada não era a chave em si: era
 * a chave num elemento que o LAYOUT dependia, no meio da coluna, empurrando
 * logo e anel a cada frase.
 *
 * Esta linha está no fim da tela, com altura reservada, e não sustenta nada
 * abaixo dela. Reanimar aqui move só ela.
 *
 * ── E a frase de progresso continua ──────────────────────────────────────
 *
 * O desfile foi ACRESCENTADO, não colocado no lugar. `mensagem` é o progresso
 * de verdade — a primeira busca ainda pendente — e trocá-la por uma vitrine
 * teria apagado a única informação da tela para caber propaganda.
 */

export interface TelaDeCargaProps {
  mensagem: string;
  /** Aplica o tom mais discreto da frase longa do aviso de lentidão. */
  acordando?: boolean;
  /**
   * O dado chegou e a tela está desaparecendo.
   *
   * Ela continua montada durante a saída — sem isso não há elemento para
   * animar. Ver `utils/saidaDaCarga.ts`.
   */
  saindo?: boolean;
}

const TelaDeCarga: React.FC<TelaDeCargaProps> = ({ mensagem, acordando, saindo }) => {
  /*
   * Onde o desfile começa — sorteado UMA vez, no primeiro render.
   *
   * `useRef` com inicialização preguiçosa, e não `useState(Math.random())`:
   * o valor não muda mais, então não há razão para ele participar de render
   * nenhum. Sem o sorteio, quem abre o app dez vezes por dia lê a primeira
   * frase dez vezes — a espera normal só dá tempo de uma.
   */
  const inicio = useRef<number | null>(null);
  if (inicio.current === null) inicio.current = Math.floor(Math.random() * SERVICOS.length);

  const [passadas, setPassadas] = useState(0);

  /*
   * O relógio para de contar quando a tela está SAINDO.
   *
   * Durante os 280ms da saída ela ainda está montada, e uma frase trocando
   * enquanto tudo desaparece é movimento que ninguém pediu.
   */
  useEffect(() => {
    if (saindo) return;
    const t = setInterval(() => setPassadas(n => n + 1), TEMPO_DE_CADA_MS);
    return () => clearInterval(t);
  }, [saindo]);

  const servico = servicoDaVez(passadas, inicio.current);

  return (
  <div className={`app-loading-overlay${saindo ? ' saindo' : ''}`} role="status" aria-live="polite">
    {/* O gráfico do logo, grande, atrás de tudo — ver `GraficoDoLogo.tsx`. */}
    <GraficoDoLogo variante="carga" />

    <div className="app-loading-box">
      <div className="app-loading-logo">
        <span className="logo-fin">Fin</span><span className="logo-track">Tracking</span>
      </div>

      {/*
        O STATUS: anel pequeno e a frase de progresso, juntos e discretos.

        Eram um anel de 64px e, embaixo, a frase com o mesmo peso da vitrine de
        serviços logo depois — duas frases centralizadas, de tamanho quase
        igual, a 40px uma da outra. Liam como dois avisos de carregamento, e a
        segunda parecia dizer que o app estava "carregando a projeção".

        Agora o status é uma coisa só, e fica longe da vitrine.
      */}
      <div className="app-loading-status">
        <svg viewBox="0 0 24 24" className="app-loading-ring" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" strokeWidth="2.5" className="app-loading-track" />
          <circle cx="12" cy="12" r="9" fill="none" strokeWidth="2.5" className="app-loading-arc" />
        </svg>
        <p className={`app-loading-msg${acordando ? ' acordando' : ''}`}>
          {mensagem}
        </p>
      </div>
    </div>

    {/*
      A VITRINE: o que o app faz, embaixo, com rótulo próprio.

      O rótulo é o que desfaz a confusão — sem ele, "Projeção do seu saldo, mês
      a mês" é só mais uma frase sob um ícone girando.

      `aria-hidden` porque o overlay inteiro é `aria-live="polite"`: sem isto,
      um leitor de tela anunciaria um serviço novo a cada 2,6 segundos por cima
      da mensagem de progresso, que é a que importa.
    */}
    <div className="app-loading-vitrine" aria-hidden="true">
      <span className="app-loading-vitrine-rotulo">No FinTracking você tem</span>
      <p className="app-loading-servicos">
        <span key={servico} className="app-loading-servico">{servico}</span>
      </p>
    </div>
  </div>
  );
};

export default TelaDeCarga;
